import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTelegramBackButton } from '@/telegram';
import { trackEvent } from '@/shared/lib/analytics';
import { StackFrame } from '@/shared/components/ui/StackFrame';
import { StackHeader } from '@/shared/components/ui/StackHeader';
import type { InstallGuidePlatform } from './runtime';

type GuideVariant = Exclude<InstallGuidePlatform, 'unknown'> | 'neutral';

interface GuideStep {
  title: string;
  description: string;
  imageSrc: string;
}

const IOS_STEPS: GuideStep[] = [
  {
    title: 'Откройте меню Safari',
    description: 'В нижней панели браузера откройте меню действий.',
    imageSrc: '/install-guide/ios-step-1.jpg',
  },
  {
    title: 'Нажмите «Поделиться»',
    description: 'Откройте список действий Safari.',
    imageSrc: '/install-guide/ios-step-2.jpg',
  },
  {
    title: 'Выберите «Добавить на экран Домой»',
    description: 'Прокрутите список, если пункт не виден сразу.',
    imageSrc: '/install-guide/ios-step-3.jpg',
  },
  {
    title: 'Подтвердите добавление',
    description: 'После этого Inhound появится на домашнем экране.',
    imageSrc: '/install-guide/ios-step-4.jpg',
  },
];

const ANDROID_STEPS: GuideStep[] = [
  {
    title: 'Откройте меню Chrome',
    description: 'Нажмите на три точки в правом верхнем углу браузера.',
    imageSrc: '/install-guide/android-step-1.jpg',
  },
  {
    title: 'Выберите «Добавить на гл. экран»',
    description: 'В некоторых версиях Chrome пункт может называться «Установить приложение».',
    imageSrc: '/install-guide/android-step-2.jpg',
  },
  {
    title: 'Подтвердите установку',
    description: 'После подтверждения приложение появится на экране телефона.',
    imageSrc: '/install-guide/android-step-3.jpg',
  },
];

function resolveGuideVariant(pathname: string, routePlatform: string | undefined): GuideVariant {
  if (routePlatform === 'ios' || pathname === '/install/ios') {
    return 'ios';
  }

  if (routePlatform === 'android' || pathname === '/install/android') {
    return 'android';
  }

  return 'neutral';
}

function getGuideCopy(variant: GuideVariant) {
  if (variant === 'ios') {
    return {
      label: 'iPhone',
      title: 'Как установить Inhound на iPhone',
      intro: 'Откройте Inhound в Safari и повторите шаги ниже.',
      steps: IOS_STEPS,
    };
  }

  if (variant === 'android') {
    return {
      label: 'Android',
      title: 'Как установить Inhound на Android',
      intro: 'Откройте Inhound в Chrome и повторите эти шаги.',
      steps: ANDROID_STEPS,
    };
  }

  return {
    label: 'Выберите платформу',
    title: 'Установка Inhound',
    intro: 'Выберите устройство ниже, чтобы открыть пошаговую инструкцию.',
    steps: [] as GuideStep[],
  };
}

function PlatformSwitch({ activeVariant }: { activeVariant: GuideVariant }) {
  const location = useLocation();

  function trackSwitch(nextPlatform: 'ios' | 'android') {
    if (activeVariant === nextPlatform) {
      return;
    }

    trackEvent('install_guide_platform_switch', {
      from: activeVariant,
      to: nextPlatform,
      pathname: location.pathname,
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Link
        to="/install/ios"
        onClick={() => trackSwitch('ios')}
        className={[
          'inline-flex min-h-11 items-center justify-center rounded-full px-4 text-[12px] font-semibold tracking-[0.02em]',
          activeVariant === 'ios'
            ? 'bg-[var(--foreground)] text-white'
            : 'border border-[rgba(64,45,14,0.12)] bg-white text-[rgba(26,26,26,0.74)]',
        ].join(' ')}
      >
        iPhone
      </Link>
      <Link
        to="/install/android"
        onClick={() => trackSwitch('android')}
        className={[
          'inline-flex min-h-11 items-center justify-center rounded-full px-4 text-[12px] font-semibold tracking-[0.02em]',
          activeVariant === 'android'
            ? 'bg-[var(--foreground)] text-white'
            : 'border border-[rgba(64,45,14,0.12)] bg-white text-[rgba(26,26,26,0.74)]',
        ].join(' ')}
      >
        Android
      </Link>
    </div>
  );
}

export function InstallGuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ platform?: string }>();
  const variant = resolveGuideVariant(location.pathname, params.platform);
  const copy = getGuideCopy(variant);

  useTelegramBackButton(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    void navigate('/catalog', { replace: true });
  }, true);

  useEffect(() => {
    trackEvent('install_guide_open', {
      platform: variant === 'neutral' ? 'unknown' : variant,
      pathname: location.pathname,
    });
  }, [location.pathname, variant]);

  return (
    <StackFrame
      container="readable-content"
      className="pb-[calc(var(--content-safe-area-bottom)+1.25rem)]"
    >
      <StackHeader
        onBack={() => {
          if (window.history.length > 1) {
            navigate(-1);
            return;
          }

          void navigate('/catalog', { replace: true });
        }}
        label="PWA"
        title="Установка приложения"
      />

      <div className="space-y-5 py-5">
        <section className="rounded-[30px] border border-[rgba(64,45,14,0.10)] bg-[linear-gradient(180deg,rgba(255,248,236,0.98)_0%,rgba(255,252,246,0.94)_100%)] px-5 py-5 shadow-[0_18px_32px_rgba(62,48,23,0.08)]">
          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[rgba(94,81,58,0.54)]">
            {copy.label}
          </p>
          <h1 className="pt-2 text-[26px] font-black tracking-[-0.03em] text-[var(--foreground)]">
            {copy.title}
          </h1>
          <p className="pt-2 text-[14px] leading-6 text-[rgba(26,26,26,0.66)]">{copy.intro}</p>
          <div className="pt-4">
            <PlatformSwitch activeVariant={variant} />
          </div>
        </section>

        {variant === 'neutral' ? (
          <section className="rounded-[26px] border border-dashed border-[rgba(64,45,14,0.14)] bg-white/86 px-5 py-5">
            <p className="text-[15px] font-semibold text-[var(--foreground)]">
              Выберите своё устройство
            </p>
            <p className="pt-2 text-[13px] leading-5 text-[rgba(26,26,26,0.66)]">
              Если вы открыли ссылку не на том устройстве или браузер не определил платформу,
              переключитесь вручную.
            </p>
          </section>
        ) : (
          <section className="space-y-4">
            {copy.steps.map((step, index) => (
              <article
                key={step.imageSrc}
                className="overflow-hidden rounded-[28px] border border-[rgba(64,45,14,0.10)] bg-white shadow-[0_16px_30px_rgba(62,48,23,0.06)]"
              >
                <div className="px-5 py-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[rgba(94,81,58,0.48)]">
                    Шаг {index + 1}
                  </p>
                  <h2 className="pt-1 text-[18px] font-black tracking-[-0.02em] text-[var(--foreground)]">
                    {step.title}
                  </h2>
                  <p className="pt-2 text-[13px] leading-5 text-[rgba(26,26,26,0.66)]">
                    {step.description}
                  </p>
                </div>
                <div className="mx-4 mb-4 overflow-hidden rounded-[24px] border border-[rgba(64,45,14,0.10)] bg-[linear-gradient(180deg,rgba(248,241,229,0.92)_0%,rgba(255,255,255,0.96)_100%)] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                  <img
                    src={step.imageSrc}
                    alt={step.title}
                    className="block w-full rounded-[18px] border border-[rgba(64,45,14,0.08)] bg-[var(--app-canvas)] object-contain shadow-[0_10px_22px_rgba(62,48,23,0.10)]"
                  />
                </div>
              </article>
            ))}
            <section className="rounded-[24px] border border-[rgba(64,45,14,0.10)] bg-[rgba(255,248,236,0.72)] px-5 py-4">
              <p className="text-[14px] font-semibold text-[var(--foreground)]">
                После установки возвращайтесь в клубы через иконку Inhound на экране телефона.
              </p>
            </section>
          </section>
        )}
      </div>
    </StackFrame>
  );
}
